/**
 * A tiny Web Audio synthesiser shared by the prototypes.
 *
 * Sounds are generated rather than loaded because at this stage the thing worth
 * proving is that the *audio path* works on the target device - that autoplay
 * policy lets us make noise after a gesture, and that timing is tight enough
 * for feedback to feel connected to the tap. Real voice-over and recorded
 * effects are an asset-pipeline question, and belong with prototype 3.
 *
 * Semitones are relative to A4 (440 Hz): 0 = A4, 12 = A5, -12 = A3.
 */

export interface ToneOptions {
  semitone: number;
  /** Seconds. Default 0.25. */
  duration?: number;
  type?: OscillatorType;
  /** Peak gain, 0-1. Default 0.22. */
  gain?: number;
  /** Seconds from now to start. Default 0. */
  delay?: number;
  /** Glide to this semitone over the duration - used for sweeps. */
  glideTo?: number;
}

export interface SequenceOptions {
  /** Seconds between note starts. Default 0.08. */
  stagger?: number;
  duration?: number;
  type?: OscillatorType;
  gain?: number;
  delay?: number;
}

export interface Synth {
  /** Must be called from inside a user gesture before the first sound. */
  unlock(): void;
  tone(options: ToneOptions): void;
  /** Play notes in order - arpeggios, stings, jingles. */
  sequence(semitones: number[], options?: SequenceOptions): void;
  destroy(): void;
}

const frequency = (semitone: number) => 440 * Math.pow(2, semitone / 12);

export function createSynth(): Synth {
  let ctx: AudioContext | null = null;

  const ensure = (): AudioContext | null => {
    if (!ctx) {
      const Ctor =
        window.AudioContext ??
        (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return null;
      ctx = new Ctor();
    }
    // Browsers start the context suspended until a gesture resumes it.
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
  };

  const tone = (options: ToneOptions): void => {
    const audio = ensure();
    if (!audio) return;

    const {
      semitone,
      duration = 0.25,
      type = 'triangle',
      gain = 0.22,
      delay = 0,
      glideTo,
    } = options;

    const start = audio.currentTime + delay;
    const osc = audio.createOscillator();
    const envelope = audio.createGain();

    osc.type = type;
    osc.frequency.setValueAtTime(frequency(semitone), start);
    if (glideTo !== undefined) {
      osc.frequency.exponentialRampToValueAtTime(frequency(glideTo), start + duration);
    }

    // Percussive envelope. exponentialRamp cannot touch zero, hence 0.0001 -
    // and the quick attack matters: a slow one makes taps feel laggy even when
    // the frame timing is fine.
    envelope.gain.setValueAtTime(0.0001, start);
    envelope.gain.exponentialRampToValueAtTime(gain, start + 0.012);
    envelope.gain.exponentialRampToValueAtTime(0.0001, start + duration);

    osc.connect(envelope).connect(audio.destination);
    osc.start(start);
    osc.stop(start + duration + 0.05);
  };

  return {
    unlock() {
      ensure();
    },

    tone,

    sequence(semitones, options = {}) {
      const { stagger = 0.08, delay = 0, ...rest } = options;
      semitones.forEach((semitone, i) => {
        tone({ ...rest, semitone, delay: delay + i * stagger });
      });
    },

    destroy() {
      void ctx?.close();
      ctx = null;
    },
  };
}
